import React, { useEffect, useState } from 'react';

/**
 * Dev-only floating badge that surfaces the axios dedupe/SWR cache health
 * (from `window.__apiDedupeStats()`, installed by `src/api.js`). Shows cache
 * hits, inflight merges, stale-while-revalidate serves, and real network
 * misses. Useful for verifying that a page isn't quietly busting the cache.
 *
 * Rendering is gated on `NODE_ENV !== 'production'` AND an opt-in flag so it
 * never shows in deployed builds.
 *   • Enable with `localStorage.setItem('rxr_show_dedupe_stats', '1')`.
 *   • Disable with `localStorage.removeItem('rxr_show_dedupe_stats')`.
 * Press the badge to collapse / expand.
 */
const ApiDedupeStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [enabled] = useState(() => {
    try {
      return (
        process.env.NODE_ENV !== 'production' &&
        typeof window !== 'undefined' &&
        window.localStorage &&
        window.localStorage.getItem('rxr_show_dedupe_stats') === '1'
      );
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      try {
        const next =
          typeof window.__apiDedupeStats === 'function'
            ? window.__apiDedupeStats()
            : null;
        setStats(next);
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled]);

  if (!enabled || !stats) return null;

  const total =
    (stats.hits || 0) +
    (stats.inflightMerges || 0) +
    (stats.staleServed || 0) +
    (stats.misses || 0);
  const hitRatePct = total === 0
    ? '—'
    : (
        (((stats.hits || 0) + (stats.inflightMerges || 0) + (stats.staleServed || 0)) /
          total) *
        100
      ).toFixed(0) + '%';

  const wrapStyle = {
    position: 'fixed',
    bottom: 12,
    right: 12,
    zIndex: 2147483000,
    background: 'rgba(17, 24, 39, 0.92)',
    color: '#e5e7eb',
    borderRadius: 8,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 11,
    padding: collapsed ? '4px 8px' : '8px 10px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    minWidth: collapsed ? 0 : 170,
    userSelect: 'none',
  };

  return (
    <div
      style={wrapStyle}
      onClick={() => setCollapsed((c) => !c)}
      title="Click to toggle. localStorage.removeItem('rxr_show_dedupe_stats') to hide."
    >
      {collapsed ? (
        <span>api {hitRatePct}</span>
      ) : (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            api cache · hit rate {hitRatePct}
          </div>
          <div>hits: {stats.hits || 0}</div>
          <div>inflight merges: {stats.inflightMerges || 0}</div>
          <div>stale served: {stats.staleServed || 0}</div>
          <div>misses: {stats.misses || 0}</div>
          <div style={{ marginTop: 4, color: '#9ca3af' }}>total: {total}</div>
        </div>
      )}
    </div>
  );
};

export default ApiDedupeStatsPanel;
